'use client';

import {useModifyCaptionsControls} from '@components/@shared/VideoPlayer/@hooks/useModifyCaptionsControls';
import {
    IVideoPlayerState,
    useVideoPlayer,
    useVideoPlayerState,
    withVideoPlayer,
} from '@components/@shared/VideoPlayer/useVideoPlayer';
import {useTrackUIMutation} from '@hooks';
import {useRunOnce} from '@hooks/useRunOnce';
import classNames from 'classnames';
import 'jb-videojs-hls-quality-selector';
import {
    ComponentProps,
    FC,
    PropsWithChildren,
    useEffect,
    useLayoutEffect,
    useRef,
} from 'react';
import videojs from 'video.js';
import Player from 'video.js/dist/types/player';
import {SourceObject} from 'video.js/dist/types/tech/tech';
import 'video.js/dist/video-js.css';
import {VideoPlayerControls} from './Controls/VideoPlayerControls';
import {VideoPlayerPoster} from './Poster/VideoPlayerPoster';
import styles from './VideoPlayer.module.css';

interface IProps extends ComponentProps<'div'>, PropsWithChildren {
    src?: SourceObject;
    isLive?: boolean;
    classNamePlayer?: string;
    isLiveRecording?: boolean;
    poster?: string | null;
    autoPlay?: boolean;
    hasSquareBorders?: boolean;
    position?: number;
    aspectRatio?: '9:16' | '16:9';
    loop?: boolean;
    metadata?: IVideoPlayerState['metadata'];
}

// default values exceed maximum buffer length (~200MB) for high bitrate 4k streams
(videojs as any).Vhs.GOAL_BUFFER_LENGTH = 10; // Default: 30
(videojs as any).Vhs.MAX_GOAL_BUFFER_LENGTH = 20; // Default: 60

export const VideoPlayer: FC<IProps> = withVideoPlayer(
    ({
        src,
        poster,
        isLive = false,
        autoPlay = false,
        classNamePlayer,
        children,
        isLiveRecording,
        hasSquareBorders,
        position,
        loop = false,
        aspectRatio = '16:9',
        metadata,
    }) => {
        const videoJsContainerRef = useRef<HTMLDivElement>(null);
        const htmlVideoRef = useRef<HTMLVideoElement>(null);
        const isReady = useVideoPlayerState(s => s.isReady);
        const player = useVideoPlayer();
        const isShort = aspectRatio === '9:16';

        const areControlsVisible = useVideoPlayerState(
            s => s.areControlsVisible,
        );

        // This has to run on first render to prevent player from seeing
        // previous player's state, as effect cleanup function fires too late in
        // the lifecycle.
        useRunOnce(player._cleanupTransient);

        useLayoutEffect(() => {
            const videoJsPlayer: Player = videojs(htmlVideoRef.current!, {
                controls: false,
                textTrackSettings: false,
                playbackRates: [1.5, 1.25, 1, 0.75, 0.5],
                autoplay: autoPlay ? 'any' : undefined,
                ...(isLive && {
                    liveui: true,
                    playbackRates: false,
                    muted: true,
                }),
                html5: {
                    vhs: {
                        limitRenditionByPlayerDimensions: false, // No player size limits,
                        useDevicePixelRatio: true,
                    },
                },
                preload: 'true',
            });

            player._onPlayerCreated(videoJsPlayer);
            return () => {
                player._cleanup();
            };
        }, []);

        useEffect(() => {
            if (!isReady || !src) {
                return;
            }
            player.load({
                source: src,
                position,
                autoplay: autoPlay,
                metadata,
            });
        }, [src, isReady, metadata, position]);

        useEffect(() => {
            if (!isReady) {
                return;
            }

            const textTrackDisplay = videoJsContainerRef.current?.querySelector(
                '.vjs-text-track-display',
            );
            if (textTrackDisplay) {
                (textTrackDisplay as HTMLElement).style.bottom =
                    areControlsVisible ? '5.5rem' : '2rem';
            }
        }, [areControlsVisible, isReady]);

        useModifyCaptionsControls();
        useTrackUIMutation(videoJsContainerRef, player._onUIChange);

        return (
            <div
                className={classNames(styles.container, {
                    [styles.hasSquareBorders]: hasSquareBorders,
                    [styles.shortContainer]: isShort,
                })}
            >
                <div data-vjs-player={true} ref={videoJsContainerRef}>
                    {children}
                    <VideoPlayerPoster
                        poster={poster}
                        size={aspectRatio === '9:16' ? '450x800' : '800x450'}
                    />
                    <VideoPlayerControls
                        aspectRatio={aspectRatio}
                        isLive={isLive}
                        isLiveRecording={isLiveRecording}
                    />
                    <video
                        className={classNames(
                            'video-js vjs-big-play-centered',
                            `vjs-${aspectRatio.replace(':', '-')}`,
                            styles.videoPlayer,
                            classNamePlayer,
                        )}
                        playsInline
                        loop={loop}
                        ref={htmlVideoRef}
                    />
                </div>
            </div>
        );
    },
);
